import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { articleTranslations } from '@/mocks/articleTranslations';
import type { ArticleData } from '@/mocks/articles';

function getLocalizedArticle(article: ArticleData, lang: string): ArticleData {
  const localized = articleTranslations[article.slug]?.[lang];
  if (!localized) return article;
  return { ...article, ...localized };
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const article = useMemo(() => {
    const base = articles.find((a) => a.slug === slug);
    if (!base) return null;
    const lang = i18n.language?.split('-')[0] || 'en';
    return getLocalizedArticle(base, lang);
  }, [slug, i18n.language]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto max-w-3xl px-4 md:px-6 pt-32 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">{t('article.notFound')}</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-primary-600 font-semibold">
            <i className="ri-arrow-left-line"></i> {t('article.backHome')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <article className="mx-auto max-w-3xl px-4 md:px-6 pt-28 md:pt-32 pb-16">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              {t('article.breadcrumbHome')}
            </Link>
            <span className="flex w-4 h-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-700">{t('article.breadcrumbGuides')}</span>
          </nav>

          <h1 className="mt-6 text-2xl md:text-4xl font-heading font-extrabold text-foreground-950 leading-tight">
            {article.h1}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-2">
              <span className="flex w-4 h-4 items-center justify-center">
                <i className="ri-calendar-line text-sm"></i>
              </span>
              {t('article.updated')}: {article.updated}
            </span>
            <span className="flex items-center gap-2">
              <span className="flex w-4 h-4 items-center justify-center">
                <i className="ri-time-line text-sm"></i>
              </span>
              {article.readingMinutes} {t('article.readingTime')}
            </span>
          </div>

          <div className="mt-8 rounded-lg bg-primary-50 border border-primary-200 p-5 md:p-6">
            <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{article.intro}</p>
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">{t('article.keyTakeaways')}</h2>
            <ul className="space-y-2">
              {article.keyTakeaways.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="flex w-5 h-5 items-center justify-center text-primary-600 shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-line text-sm"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 space-y-10">
            {article.sections.map((section, index) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
                {index === article.sections.length - 1 && (
                  <div className="mt-6">
                    <Link
                      to="/spreadsheet"
                      className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t('hero.ctaPrimary')}
                      <span className="flex w-4 h-4 items-center justify-center">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </Link>
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 p-5 rounded-lg bg-background-100 border border-background-200">
            <p className="text-xs text-foreground-500 leading-relaxed">{t('footer.disclaimer')}</p>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <span className="flex w-4 h-4 items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              {t('article.backHome')}
            </Link>
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              {t('nav.guides')}
              <span className="flex w-4 h-4 items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}